import React, { useState } from 'react';
import { ArrowLeft, FileText, BookOpen, CheckCircle, X } from 'lucide-react';
import WorksheetViewer from './WorksheetViewer';
import QuizSection from './QuizSection';

export default function ModuleDetail({ module, onBack }) {
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [completedWorksheets, setCompletedWorksheets] = useState(new Set());
  const [completedQuizzes, setCompletedQuizzes] = useState(new Set());

  const handleWorksheetComplete = (worksheetId) => {
    setCompletedWorksheets(prev => new Set([...prev, worksheetId]));
  };

  const handleQuizComplete = (quizId) => {
    setCompletedQuizzes(prev => new Set([...prev, quizId]));
  };

  const completedCount = completedWorksheets.size + completedQuizzes.size;
  const totalCount = (module.worksheets?.length || 0) + (module.quizzes?.length || 0);
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Combine worksheets and quizzes, sort by order
  const allContent = [
    ...(module.worksheets || []).map(w => ({ ...w, type: 'worksheet' })),
    ...(module.quizzes || []).map(q => ({ ...q, type: 'quiz' }))
  ].sort((a, b) => (a.order || 999) - (b.order || 999));

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-purple-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Learning
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{module.title}</h1>
          <p className="text-xl text-gray-600 mb-4">{module.description}</p>
          
          {/* Progress Bar */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Module Progress</span>
              <span className="text-sm font-medium text-purple-600">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {completedCount} of {totalCount} activities completed
            </p>
          </div>
        </div>

        {/* Combined Worksheets and Quizzes Section */}
        {allContent.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">Worksheets & Quizzes</h2>
              <span className="ml-3 text-sm text-gray-500">
                ({allContent.length} {allContent.length === 1 ? 'item' : 'items'})
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allContent.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`rounded-lg p-3 ${item.type === 'worksheet' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {item.type === 'worksheet' ? (
                          <FileText className="w-6 h-6 text-purple-600" />
                        ) : (
                          <BookOpen className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.title}
                          </h3>
                          {(item.type === 'worksheet' && completedWorksheets.has(item.id)) ||
                           (item.type === 'quiz' && completedQuizzes.has(item.id)) ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : null}
                        </div>
                        <p className="text-sm text-gray-500">
                          {item.type === 'worksheet' ? 'Worksheet' : 'Quiz'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {item.description && (
                    <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                  )}
                  {item.type === 'worksheet' ? (
                    <button
                      onClick={() => setSelectedWorksheet(item)}
                      className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors duration-200"
                    >
                      {completedWorksheets.has(item.id) ? 'Review Worksheet' : 'Open Worksheet'}
                    </button>
                  ) : item.googleFormUrl ? (
                    <a
                      href={item.googleFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200 text-center block"
                      onClick={() => handleQuizComplete(item.id)}
                    >
                      Take Quiz on Google Forms →
                    </a>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 mb-4">
                        {item.questions?.length || 0} questions
                      </p>
                      <button
                        onClick={() => setSelectedQuiz(item)}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200"
                      >
                        {completedQuizzes.has(item.id) ? 'Retake Quiz' : 'Start Quiz'}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Content Message */}
        {(!module.worksheets || module.worksheets.length === 0) && 
         (!module.quizzes || module.quizzes.length === 0) && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Content Available</h3>
            <p className="text-gray-600">
              Worksheets and quizzes for this module are being prepared.
            </p>
          </div>
        )}
      </div>

      {/* Worksheet Viewer Modal */}
      {selectedWorksheet && (
        <WorksheetViewer
          worksheet={selectedWorksheet}
          onClose={() => {
            setSelectedWorksheet(null);
            handleWorksheetComplete(selectedWorksheet.id);
          }}
        />
      )}

      {/* Quiz Modal */}
      {selectedQuiz && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedQuiz.title}</h2>
                <button
                  onClick={() => {
                    setSelectedQuiz(null);
                    handleQuizComplete(selectedQuiz.id);
                  }}
                  className="text-gray-600 hover:text-red-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <QuizSection 
                questions={selectedQuiz.questions}
                onComplete={() => {
                  setSelectedQuiz(null);
                  handleQuizComplete(selectedQuiz.id);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

